import { api } from "@/lib/api";
import {
  type LocalSession,
  type LocalUser,
  clearSession,
  readSession,
  writeSession,
} from "@/lib/auth";
import { createContext, createSignal, onMount, useContext } from "solid-js";
import type { Accessor, JSX } from "solid-js";

import { bindThemeTenant } from "@/features/theme/apply";

import { type CapabilityMap, normalizeCapabilities } from "./capabilities";

type MeResponse = {
  userId: string;
  tenantId: string | null;
  role: string | null;
  platformAdmin?: boolean;
  capabilities: CapabilityMap;
};

export type SessionGrant = {
  access_token: string;
  expires_in: number;
  user: LocalUser & { platform_admin?: boolean };
};

export type LoginResult =
  | { status: "ok" }
  | {
      status: "mfa_setup";
      mfa_token: string;
      otpauth_url: string;
      secret: string;
      qr_data_url: string;
    }
  | { status: "mfa_required"; mfa_token: string };

type AuthContextValue = {
  session: Accessor<LocalSession | null>;
  user: Accessor<LocalUser | null>;
  loading: Accessor<boolean>;
  meLoading: Accessor<boolean>;
  role: Accessor<string | undefined>;
  capabilities: Accessor<CapabilityMap>;
  platformAdmin: Accessor<boolean>;
  signIn: (email: string, password: string) => Promise<LoginResult>;
  establishSession: (grant: SessionGrant) => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue>();

export function AuthProvider(props: { children: JSX.Element }) {
  const [session, setSession] = createSignal<LocalSession | null>(null);
  const [loading, setLoading] = createSignal(true);
  const [me, setMe] = createSignal<MeResponse | null>(null);
  const [meLoading, setMeLoading] = createSignal(false);

  async function loadMe(token: string | undefined) {
    if (!token) {
      setMe(null);
      return;
    }
    setMeLoading(true);
    try {
      setMe(await api<MeResponse>("/api/v1/me"));
    } catch {
      setMe(null);
    } finally {
      setMeLoading(false);
    }
  }

  async function establishSession(grant: SessionGrant) {
    const next: LocalSession = {
      access_token: grant.access_token,
      expires_at: Math.floor(Date.now() / 1000) + grant.expires_in,
      user: {
        ...grant.user,
        app_metadata: {
          tenant_id: grant.user.tenant_id,
          learner_id: grant.user.learner_id ?? undefined,
        },
      },
    };
    writeSession(next);
    setSession(next);
    bindThemeTenant(next.user.tenant_id ?? next.user.app_metadata?.tenant_id ?? null);
    await loadMe(next.access_token);
  }

  onMount(async () => {
    const current = readSession();
    setSession(current);
    if (current?.access_token) await loadMe(current.access_token);
    setLoading(false);
  });

  const value: AuthContextValue = {
    session,
    user: () => session()?.user ?? null,
    loading,
    meLoading,
    role: () => me()?.role ?? session()?.user?.role,
    capabilities: () => normalizeCapabilities(me()?.capabilities),
    platformAdmin: () => me()?.platformAdmin === true,
    async signIn(email, password) {
      const result = await api<LoginResult & Partial<SessionGrant>>("/api/v1/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      if (result.status === "mfa_setup" || result.status === "mfa_required") return result;
      if (!result.access_token || !result.user || result.expires_in == null) {
        throw new Error("Réponse de connexion inattendue.");
      }
      await establishSession({
        access_token: result.access_token,
        expires_in: result.expires_in,
        user: result.user,
      });
      return { status: "ok" };
    },
    establishSession,
    async signOut() {
      clearSession();
      setSession(null);
      setMe(null);
      bindThemeTenant(null);
    },
  };

  return <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
